/**
 * TermsPage.js
 * DataQuad Terms of Service — public page
 */

import React, { useEffect } from 'react';
import { useTheme } from '../utils/ThemeSwitcher';
import { ArrowLeft, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const TermsPage = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap';
    link.rel = 'stylesheet';
    if (!document.querySelector('link[href*="Outfit"]')) document.head.appendChild(link);
    window.scrollTo(0, 0);
    document.title = 'Terms of Service — Noble Syntax';
  }, []);

  const colors = {
    bg: isDarkMode ? '#08090B' : '#FFFFFF',
    cardBg: isDarkMode ? 'rgba(255,255,255,0.03)' : '#FFFFFF',
    text: isDarkMode ? '#E5E7EB' : '#1F2937',
    textSecondary: isDarkMode ? '#9CA3AF' : '#6B7280',
    heading: isDarkMode ? '#F9FAFB' : '#111827',
    border: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
    accent: '#2B8AFF',
  };

  const sectionStyle = { marginBottom: '32px' };
  const h2Style = { fontSize: '20px', fontWeight: '600', color: colors.heading, marginBottom: '12px', marginTop: 0 };
  const h3Style = { fontSize: '16px', fontWeight: '600', color: colors.heading, marginBottom: '8px', marginTop: '20px' };
  const pStyle = { fontSize: '15px', lineHeight: '1.75', color: colors.text, margin: '0 0 12px 0' };
  const liStyle = { fontSize: '15px', lineHeight: '1.75', color: colors.text, marginBottom: '6px' };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: colors.bg,
      fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif",
    }}>
      {/* Header */}
      <div style={{
        borderBottom: `1px solid ${colors.border}`,
        backgroundColor: isDarkMode ? 'rgba(8,9,11,0.92)' : 'rgba(255,255,255,0.92)',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        backdropFilter: 'blur(12px)',
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <button
            onClick={() => navigate('/')}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              background: 'none', border: 'none', cursor: 'pointer',
              color: colors.accent, fontSize: '14px', fontWeight: '500',
              fontFamily: 'inherit',
            }}
          >
            <ArrowLeft size={16} /> Back
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: colors.textSecondary, fontSize: '13px' }}>
            <FileText size={14} />
            <span>Legal</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '48px 24px 80px' }}>
        <h1 style={{ fontSize: '32px', fontWeight: '700', color: colors.heading, marginBottom: '8px' }}>Terms of Service</h1>
        <p style={{ fontSize: '14px', color: colors.textSecondary, marginBottom: '40px' }}>Last updated: January 27, 2026</p>

        <div style={{
          backgroundColor: colors.cardBg,
          borderRadius: '16px',
          border: `1px solid ${colors.border}`,
          padding: '40px',
        }}>
          <div style={sectionStyle}>
            <h2 style={h2Style}>1. Acceptance of Terms</h2>
            <p style={pStyle}>
              By accessing or using DataQuad ("the Service"), operated by Noble Syntax Limited ("we", "us", "our"),
              a company registered in Zambia (Registration No. 120251023439), you agree to be bound by these Terms of Service.
              If you do not agree, please do not use the Service.
            </p>
          </div>

          <div style={sectionStyle}>
            <h2 style={h2Style}>2. Description of Service</h2>
            <p style={pStyle}>
              DataQuad is an AI-powered data quality assessment platform for survey data. The Service provides
              data quality analysis, anomaly detection, back-check validation, data cleaning tools, and reporting
              features for data collected through KoBoToolbox, SurveyCTO, ODK, and similar platforms.
            </p>
          </div>

          <div style={sectionStyle}>
            <h2 style={h2Style}>3. Account Registration</h2>
            <p style={pStyle}>
              To access the Service, you must create an account by providing accurate and complete information.
              You are responsible for maintaining the confidentiality of your account credentials and for all
              activities that occur under your account.
            </p>
            <ul style={{ paddingLeft: '20px', margin: '12px 0' }}>
              <li style={liStyle}>You must be at least 18 years of age to use the Service</li>
              <li style={liStyle}>You must provide a valid email address</li>
              <li style={liStyle}>You are responsible for maintaining the security of your account</li>
              <li style={liStyle}>You must notify us immediately of any unauthorized use of your account</li>
            </ul>
          </div>

          <div style={sectionStyle}>
            <h2 style={h2Style}>4. Subscription Plans and Payments</h2>
            <p style={pStyle}>
              DataQuad offers tiered subscription plans (Standard, Pro, and Max) with monthly and annual
              billing options. All payments are processed securely through Paddle, our Merchant of Record.
            </p>
            <h3 style={h3Style}>4.1 Free Trial</h3>
            <p style={pStyle}>
              New subscribers receive a 15-day free trial. You will not be charged during the trial period.
              At the end of the trial, your subscription will automatically convert to a paid plan unless
              cancelled before the trial period ends.
            </p>
            <h3 style={h3Style}>4.2 Billing</h3>
            <p style={pStyle}>
              Subscription fees are billed in advance on a monthly or annual basis, depending on your chosen plan.
              All prices are in US Dollars (USD) and exclude applicable taxes, which are calculated and collected
              by Paddle based on your location.
            </p>
            <h3 style={h3Style}>4.3 Cancellation</h3>
            <p style={pStyle}>
              You may cancel your subscription at any time through your account settings or the Paddle customer portal.
              Cancellation takes effect at the end of your current billing period. You will retain access to the
              Service until the end of the period you have already paid for.
            </p>
          </div>


          <div style={sectionStyle}>
            <h2 style={h2Style}>5. Refund Policy</h2>
            <p style={pStyle}>
              We offer a 14-day refund window from the date of your first paid charge. If you are not satisfied
              with the Service, you may request a full refund within 14 days — no questions asked.
              Free trial periods are not eligible for refunds as no charge occurs.
              For refund requests, contact support@noblesyntax.com. See our full <a href="/refund" style={{ color: colors.accent }}>Refund Policy</a> for details.
            </p>
          </div>

          <div style={sectionStyle}>
            <h2 style={h2Style}>6. Your Data</h2>
            <p style={pStyle}>
              You retain full ownership of all data you upload to DataQuad. We do not claim any intellectual property
              rights over your survey data. Your data is processed solely to provide the Service and is not shared
              with third parties except as described in our <a href="/privacy" style={{ color: colors.accent }}>Privacy Policy</a>.
            </p>
            <ul style={{ paddingLeft: '20px', margin: '12px 0' }}>
              <li style={liStyle}>You can export your data at any time in CSV, Excel, or Stata formats</li>
              <li style={liStyle}>You can request deletion of your data by contacting support</li>
              <li style={liStyle}>Upon account termination, your data will be retained for 30 days before permanent deletion</li>
            </ul>
          </div>

          <div style={sectionStyle}>
            <h2 style={h2Style}>7. Acceptable Use</h2>
            <p style={pStyle}>You agree not to:</p>
            <ul style={{ paddingLeft: '20px', margin: '12px 0' }}>
              <li style={liStyle}>Use the Service for any unlawful purpose</li>
              <li style={liStyle}>Upload data containing malware or harmful code</li>
              <li style={liStyle}>Attempt to gain unauthorized access to the Service or its systems</li>
              <li style={liStyle}>Resell, sublicense, or redistribute the Service without our written consent</li>
              <li style={liStyle}>Reverse engineer or attempt to extract source code from the Service</li>
              <li style={liStyle}>Use the Service to process data in violation of applicable data protection laws</li>
            </ul>
          </div>

          <div style={sectionStyle}>
            <h2 style={h2Style}>8. Intellectual Property</h2>
            <p style={pStyle}>
              The Service, including its design, code, algorithms, logos, and documentation, is owned by Noble Syntax Limited
              and is protected by intellectual property laws. Your subscription grants you a limited, non-exclusive,
              non-transferable license to use the Service for its intended purpose.
            </p>
          </div>

          <div style={sectionStyle}>
            <h2 style={h2Style}>9. Service Availability</h2>
            <p style={pStyle}>
              We strive to maintain 99.9% uptime but do not guarantee uninterrupted availability. The Service may
              be temporarily unavailable due to maintenance, updates, or circumstances beyond our control.
              We will provide reasonable notice of planned maintenance when possible.
            </p>
          </div>

          <div style={sectionStyle}>
            <h2 style={h2Style}>10. Limitation of Liability</h2>
            <p style={pStyle}>
              To the maximum extent permitted by law, Noble Syntax Limited shall not be liable for any indirect,
              incidental, special, consequential, or punitive damages, including loss of data, profits, or business
              opportunities, arising from your use of the Service. Our total liability shall not exceed the amount
              you paid for the Service in the 12 months preceding the claim.
            </p>
          </div>

          <div style={sectionStyle}>
            <h2 style={h2Style}>11. Termination</h2>
            <p style={pStyle}>
              We reserve the right to suspend or terminate your account if you violate these Terms. Upon termination,
              your right to use the Service ceases immediately. Provisions that by their nature should survive
              termination (including ownership, liability limitations, and dispute resolution) shall survive.
            </p>
          </div>

          <div style={sectionStyle}>
            <h2 style={h2Style}>12. Governing Law</h2>
            <p style={pStyle}>
              These Terms are governed by the laws of the Republic of Zambia. Any disputes arising from these Terms
              shall be resolved in the courts of Zambia, unless otherwise required by applicable consumer protection laws.
            </p>
          </div>

          <div style={sectionStyle}>
            <h2 style={h2Style}>13. Changes to Terms</h2>
            <p style={pStyle}>
              We may update these Terms from time to time. Material changes will be communicated via email or
              a notice within the Service. Continued use of the Service after changes constitutes acceptance of
              the revised Terms.
            </p>
          </div>

          <div style={{ ...sectionStyle, marginBottom: 0 }}>
            <h2 style={h2Style}>14. Contact Us</h2>
            <p style={pStyle}>
              For questions about these Terms, please contact us:
            </p>
            <p style={{ ...pStyle, marginBottom: 0 }}>
              <strong>Noble Syntax Limited</strong><br />
              Email: support@noblesyntax.com<br />
              Website: dataquadapp.com<br />
              Location: Lusaka, Zambia
            </p>
          </div>
        </div>
      </div>

      <style>{`
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}; border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: ${isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)'}; }
      `}</style>
    </div>
  );
};

export default TermsPage;